import React, { useState, useMemo, useEffect } from 'react'
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core'
import { arrayMove, sortableKeyboardCoordinates } from '@dnd-kit/sortable'
import {
  Search,
  Users,
  AlertCircle,
  Calendar,
  XCircle,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Loader2,
  Eye,
} from 'lucide-react'
import { useGetBotAlertsQuery } from '@/redux/service/bot'
import { useGetTeamsQuery } from '@/redux/service/employee'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Button } from '@/components/ui/button'
import dayjs from 'dayjs'
import { CustomPagination } from '@/components/CustomPagination'
import BotAlertmodel from '@/components/BotAlertmodel'
import ReusableDateRangePicker, {
  DateRange,
} from '@/components/ReusableDateRangePicker'
import { cn } from '@/lib/utils'
import { ColumnVisibilityPopover } from '@/components/ColumnVisibilityPopover'

const DEFAULT_COLUMNS = [
  { key: 'user_name', label: 'User Name' },
  { key: 'total_alerts', label: 'Total Alerts' },
  { key: 'last_alert', label: 'Last Alert Date' },
]

const AlertDashboard = () => {
  const [searchTerm, setSearchTerm] = useState('')
  const [team, setTeam] = useState<string>('all')
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(100)
  const [startDate, setStartDate] = useState<string | undefined>(undefined)
  const [endDate, setEndDate] = useState<string | undefined>(undefined)
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  })
  const [filterCard, setFilterCard] = useState(true)

  const [sortBy, setSortBy] = useState<string | undefined>()
  const [order, setOrder] = useState<'ASC' | 'DESC' | undefined>()

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  )

  const [columns, setColumns] = useState<{ key: string; label: string }[]>(
    () => {
      const savedOrder = localStorage.getItem('alert-table-column-order')
      if (savedOrder) {
        try {
          const parsedOrder = JSON.parse(savedOrder)
          return parsedOrder
            .map((key: string) => DEFAULT_COLUMNS.find(c => c.key === key))
            .filter(Boolean) as { key: string; label: string }[]
        } catch {
          // Fallback
        }
      }
      return DEFAULT_COLUMNS
    }
  )

  const [selectedColumns, setSelectedColumns] = useState<string[]>(() => {
    const savedVisibility = localStorage.getItem('alert-table-columns')
    if (savedVisibility) {
      try {
        return JSON.parse(savedVisibility)
      } catch {
        // Fallback
      }
    }
    return DEFAULT_COLUMNS.map(col => col.key)
  })

  useEffect(() => {
    localStorage.setItem(
      'alert-table-column-order',
      JSON.stringify(columns.map(c => c.key))
    )
  }, [columns])

  useEffect(() => {
    localStorage.setItem('alert-table-columns', JSON.stringify(selectedColumns))
  }, [selectedColumns])

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (over && active.id !== over.id) {
      setColumns(items => {
        const oldIndex = items.findIndex(i => i.key === active.id)
        const newIndex = items.findIndex(i => i.key === over.id)
        return arrayMove(items, oldIndex, newIndex)
      })
    }
  }

  const toggleColumn = (key: string) => {
    setSelectedColumns(prev =>
      prev.includes(key)
        ? prev.filter(c => c !== key && selectedColumns.length > 1)
        : [...prev, key]
    )
  }

  const isColumnVisible = (key: string) => selectedColumns.includes(key)

  const visibleColumnsCount = useMemo(() => {
    return columns.filter(col => selectedColumns.includes(col.key)).length + 1 // +1 for Action
  }, [columns, selectedColumns])

  const handleSort = (column: string) => {
    if (sortBy === column) {
      if (order === 'ASC') {
        setOrder('DESC')
      } else if (order === 'DESC') {
        setSortBy(undefined)
        setOrder(undefined)
      }
    } else {
      setSortBy(column)
      setOrder('ASC')
    }
    setPage(1)
  }

  const getSortIcon = (column: string) => {
    if (sortBy !== column)
      return <ArrowUpDown className='w-3 h-3 ml-2 opacity-50 inline-block' />
    if (order === 'ASC')
      return <ArrowUp className='w-3 h-3 ml-2 inline-block' />
    return <ArrowDown className='w-3 h-3 ml-2 inline-block' />
  }

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<{
    id: number
    name: string
    profile_picture: string | null
  } | null>(null)

  const {
    data: alertsData,
    isFetching: isAlertsLoading,
    isLoading,
  } = useGetBotAlertsQuery({
    page,
    limit,
    search: searchTerm,
    team_id: team === 'all' ? undefined : Number(team),
    startDate,
    endDate,
    sort_by: sortBy,
    order,
  })

  const { data: teamsData } = useGetTeamsQuery({ pending: false })

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value)
    setPage(1)
  }

  const handleTeamChange = (value: string) => {
    setTeam(value)
    setPage(1)
  }

  const handleRangeSelect = (range: DateRange) => {
    setDateRange(range)
    setStartDate(
      range.from ? dayjs(range.from).format('YYYY-MM-DD') : undefined
    )
    setEndDate(range.to ? dayjs(range.to).format('YYYY-MM-DD') : undefined)
    setPage(1)
  }

  const clearFilters = () => {
    setSearchTerm('')
    setTeam('all')
    setStartDate(undefined)
    setEndDate(undefined)
    setSortBy(undefined)
    setOrder(undefined)
    setPage(1)
  }

  const handleViewDetails = (
    userId: number,
    userName: string,
    profilePicture: string | null
  ) => {
    setSelectedUser({
      id: userId,
      name: userName,
      profile_picture: profilePicture,
    })
    setIsModalOpen(true)
  }

  const totalPages = alertsData?.pagination?.totalPages || 1
  const totalRecords = alertsData?.pagination?.total || 0

  return (
    <div className='h-full flex flex-col space-y-4 p-2 overflow-hidden'>
      <div className='shrink-0 flex flex-col space-y-6'>
        <div className='flex items-center justify-between'>
          <h1 className='text-4xl font-extrabold tracking-tight text-foreground'>
            Alert Dashboard
          </h1>
          <div className='flex items-center gap-3'>
            <Button
              onClick={() => setFilterCard(prev => !prev)}
              variant='outline'
              size='sm'
              className='flex gap-2 h-8 text-xs border-border/50 hover:bg-accent/50'
            >
              Filter
            </Button>
            <ColumnVisibilityPopover
              columns={columns}
              isColumnVisible={isColumnVisible}
              onToggle={toggleColumn}
              onReset={() => {
                setColumns(DEFAULT_COLUMNS)
                setSelectedColumns(DEFAULT_COLUMNS.map(c => c.key))
              }}
              onDragEnd={handleDragEnd}
              sensors={sensors}
            />
          </div>
        </div>
      </div>

      {filterCard && (
        <div className='shrink-0 border border-border rounded-xl bg-card p-6 shadow-sm space-y-5'>
          <div className='grid grid-cols-1 md:grid-cols-3 gap-4'>
            <div className='relative flex-1'>
              <Search className='absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500' />
              <Input
                placeholder='Search user...'
                value={searchTerm}
                onChange={handleSearchChange}
                className='pl-10 h-10 border-border text-foreground focus:border-primary transition-all rounded-lg'
              />
            </div>
            <div className='w-full'>
              <Select value={team} onValueChange={handleTeamChange}>
                <SelectTrigger className='h-10 border-border text-foreground rounded-lg'>
                  <div className='flex items-center gap-2'>
                    <Users className='w-4 h-4 text-slate-500' />
                    <SelectValue placeholder='Team Filter' />
                  </div>
                </SelectTrigger>
                <SelectContent className='border-border bg-popover'>
                  <SelectItem value='all'>All Teams</SelectItem>
                  {teamsData?.data?.map(t => (
                    <SelectItem key={t.id} value={String(t.id)}>
                      {t.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className='flex items-center gap-2'>
              <ReusableDateRangePicker
                value={dateRange}
                onSelect={handleRangeSelect}
                placeholder='Alert Range'
                className='h-10 border-border rounded-lg flex-1'
              />
              {(searchTerm ||
                team !== 'all' ||
                startDate ||
                endDate ||
                sortBy) && (
                <Button
                  variant='ghost'
                  size='sm'
                  onClick={clearFilters}
                  className='text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors flex items-center gap-2'
                >
                  <XCircle className='w-4 h-4' /> Clear
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      <div className='flex-1 rounded-xl border border-border bg-card overflow-auto shadow-sm '>
        <Table className='[&_th]:h-9 [&_th]:px-3 [&_td]:px-3 [&_td]:py-2 border-none'>
          <TableHeader className='bg-muted/50'>
            <TableRow className='hover:bg-muted/50 border-border'>
              {columns.map(column => {
                if (!isColumnVisible(column.key)) return null
                const sortKeyMap: Record<string, string> = {
                  user_name: 'user_name',
                  total_alerts: 'missing_days_count',
                  last_alert: 'alert_date',
                }
                const sortKey = sortKeyMap[column.key]
                return (
                  <TableHead
                    key={column.key}
                    className={`text-muted-foreground font-medium py-4 ${sortKey ? 'cursor-pointer select-none' : ''}`}
                    onClick={sortKey ? () => handleSort(sortKey) : undefined}
                  >
                    <div
                      className={cn(
                        'flex items-center',
                        column.key === 'total_alerts' && 'justify-center'
                      )}
                    >
                      {column.label} {sortKey && getSortIcon(sortKey)}
                    </div>
                  </TableHead>
                )
              })}
              <TableHead className='text-muted-foreground font-medium  '>
                Action
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isAlertsLoading || isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnsCount}
                  className='h-[200px] text-center'
                >
                  <Loader2 className='w-6 h-6 text-primary animate-spin mx-auto' />
                </TableCell>
              </TableRow>
            ) : alertsData?.data?.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={visibleColumnsCount}
                  className='h-[400px] text-center'
                >
                  <AlertCircle className='w-10 h-10 text-muted-foreground/40' />
                  <p className='text-sm font-medium text-muted-foreground'>
                    No alerts recorded
                  </p>
                  <p className='text-xs text-muted-foreground/60'>
                    Try refining your filter selection
                  </p>
                </TableCell>
              </TableRow>
            ) : (
              alertsData?.data?.map(alert => (
                <TableRow
                  key={alert.user_id}
                  className='hover:bg-muted/50 border-border'
                >
                  {columns.map(column => {
                    if (!isColumnVisible(column.key)) return null
                    switch (column.key) {
                      case 'user_name':
                        return (
                          <TableCell key={column.key} className=' font-medium'>
                            <div className='flex items-center gap-3 '>
                              <div className='w-8 h-8 rounded-full overflow-hidden bg-primary/10 flex items-center justify-center border border-border shrink-0 shadow-sm group-hover:scale-105 transition-transform duration-200'>
                                {alert.profile_picture ? (
                                  <img
                                    src={alert.profile_picture}
                                    alt={alert.user_name}
                                    className='w-full h-full object-cover'
                                    onError={e => {
                                      ;(
                                        e.target as HTMLImageElement
                                      ).style.display = 'none'
                                    }}
                                  />
                                ) : (
                                  <span className='text-xs font-bold text-primary'>
                                    {alert.user_name.charAt(0).toUpperCase()}
                                  </span>
                                )}
                              </div>
                              <span className='font-bold text-foreground'>
                                {alert.user_name}
                              </span>
                            </div>
                          </TableCell>
                        )
                      case 'total_alerts':
                        return (
                          <TableCell key={column.key} className=' text-center'>
                            <span
                              className={cn(
                                'px-2.5 py-1 rounded-full text-xs font-semibold border',
                                Number(alert?.missing_days_count) > 5 &&
                                  'bg-red-500/10 text-red-400 border-red-500/20',
                                Number(alert?.missing_days_count) > 2 &&
                                  Number(alert?.missing_days_count) <= 5 &&
                                  'bg-yellow-400/10 text-yellow-400 border-yellow-400/20',
                                Number(alert?.missing_days_count) <= 2 &&
                                  'bg-green-500/10 text-green-400 border-green-500/20'
                              )}
                            >
                              {alert.missing_days_count} Alerts
                            </span>
                          </TableCell>
                        )
                      case 'last_alert':
                        return (
                          <TableCell key={column.key}>
                            <div className='flex items-center gap-2 text-muted-foreground font-medium whitespace-nowrap'>
                              <Calendar className='w-4 h-4' />
                              {dayjs(alert.alert_date).format('DD MMM YYYY')}
                            </div>
                          </TableCell>
                        )
                      default:
                        return null
                    }
                  })}
                  <TableCell>
                    <Button
                      variant='outline'
                      size='sm'
                      onClick={() =>
                        handleViewDetails(
                          alert.user_id,
                          alert.user_name,
                          alert.profile_picture
                        )
                      }
                      className='w-11 bg-background border-border hover:bg-accent hover:text-accent-foreground'
                    >
                      <Eye className='w-4 h-4' />
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {!isAlertsLoading && !isLoading && totalPages >= 1 && (
        <div className='shrink-0'>
          <CustomPagination
            page={page}
            setPage={setPage}
            limit={limit}
            setLimit={setLimit}
            totalPages={totalPages}
            totalRecords={totalRecords}
            title='Alerts'
          />
        </div>
      )}

      <BotAlertmodel
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        userId={selectedUser?.id || null}
        profile={selectedUser?.profile_picture || null}
        userName={selectedUser?.name || null}
      />
    </div>
  )
}

export default AlertDashboard
